import {AttachmentUploadUrlResponse} from "@/shared/timr";

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState
 */
const READYSTATE_DONE = 4;

/**
 * Result of uploading a single file to S3.
 * `success` is only true if S3 acknowledged the upload with an HTTP 200 response. Callers MUST use
 * this to decide whether to confirm the upload with the backend: confirming a failed upload marks the
 * attachment as present while the object is missing in S3 (e.g. expired presigned URL, network error),
 * which later surfaces as a broken download.
 */
export interface S3UploadResult {
	attachmentId: string;
	fileName: string;
	success: boolean;
}

export function uploadToS3(files: File[], uploadResponses: AttachmentUploadUrlResponse[], confirmCallback?: (attachmentId: string) => void): Promise<S3UploadResult[]> {
	return Promise.all(
		files.map((file, index): Promise<S3UploadResult> => {
			const uploadResponse = uploadResponses[index]
			const url = uploadResponse.url;
			const signedHeaders = uploadResponse.signedHeaders;

			return uploadFileToS3(file, url, signedHeaders)
				.then(() => {
					confirmCallback?.(uploadResponse.attachmentId);
					return {attachmentId: uploadResponse.attachmentId, fileName: file.name, success: true};
				}, (e) => {
					console.error("Error uploading file", file, e);
					return {attachmentId: uploadResponse.attachmentId, fileName: file.name, success: false};
				});
		})
	);
}

function uploadFileToS3(file: File, url: string, signedHeaders: { [key: string]: string[] }): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		// It's easier to monitor progress using XMLHttpRequest than using fetch
		const xhr = new XMLHttpRequest();

		xhr.onerror = (_) => {
			reject(new Error("Upload failed"));
		}
		xhr.onreadystatechange = (_) => {
			if (xhr.readyState === READYSTATE_DONE) {
				if (xhr.status === 200) {
					resolve();
				} else {
					reject(new Error("Upload failed with status: " + xhr.status));
				}
			}
		}

		xhr.open("PUT", url, true); // Set async

		// Set automatically by the browser and will otherwise produce a warning in the console
		delete signedHeaders["content-length"];
		delete signedHeaders["host"];

		// Must be set after open
		for (const key in signedHeaders) {
			if (Object.prototype.hasOwnProperty.call(signedHeaders, key)) {
				signedHeaders[key].forEach(value => {
					xhr.setRequestHeader(key, value);
				});
			}
		}

		xhr.send(file);
	});
}

export function formatFileSize(size: number): string {
	if (size < 1000) {
		return size + " B";
	} else if (size < 1_000_000) {
		return (size / 1000).toFixed(2) + " KB";
	} else if (size < 1_000_000_000) {
		return (size / (1_000_000)).toFixed(2) + " MB";
	} else {
		return (size / (1_000_000_000)).toFixed(2) + " GB";
	}
}
